import type { Dayjs } from 'dayjs';

import dayjs from 'dayjs';

export type DayGroup<T> = {
  dateKey: string; // 'YYYY-MM-DD'
  label: string;
  items: T[];
};

/** The words a day heading uses. `older` writes a day before yesterday. */
export type DayLabels = {
  today: string;
  yesterday: string;
  older: (isoTimestamp: string) => string;
};

export const ENGLISH_DAY_LABELS: DayLabels = {
  today: 'Today',
  yesterday: 'Yesterday',
  older: (isoTimestamp) => dayjs(isoTimestamp).format('MMM D, YYYY'),
};

/** One group per calendar day, in the order the items arrive. */
export function groupByDay<T>(
  items: T[],
  timestampOf: (item: T) => string,
  now: Dayjs = dayjs(),
  labels: DayLabels = ENGLISH_DAY_LABELS
): DayGroup<T>[] {
  const today = now.format('YYYY-MM-DD');
  const yesterday = now.subtract(1, 'day').format('YYYY-MM-DD');

  const groups: DayGroup<T>[] = [];
  const byKey = new Map<string, DayGroup<T>>();

  for (const item of items) {
    const timestamp = timestampOf(item);
    const dateKey = dayjs(timestamp).format('YYYY-MM-DD');
    let group = byKey.get(dateKey);
    if (!group) {
      const label =
        dateKey === today ? labels.today : dateKey === yesterday ? labels.yesterday : labels.older(timestamp);
      group = { dateKey, label, items: [] };
      byKey.set(dateKey, group);
      groups.push(group);
    }
    group.items.push(item);
  }

  return groups;
}
